import React, { useEffect, useState } from "react";
import {
  Button,
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Paper,
  CircularProgress,
} from "@material-ui/core";
import SchooleRegistration from "./SchoolRegistration";
import SchooleRegistrationService from "./Locality/Service/schooleRegistration";
import SchoolIcon from "@material-ui/icons/School";
import PeopleIcon from "@material-ui/icons/People";
import PersonIcon from "@material-ui/icons/Person";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import AssignmentIcon from "@material-ui/icons/Assignment";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import EmailIcon from "@material-ui/icons/Email";
import PhoneIcon from "@material-ui/icons/Phone";

const summaryCardStyle = (bgColor) => ({
  height: "100%",
  borderRadius: 12,
  background: bgColor,
  color: "#fff",
  padding: 16,
  boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
});

const schoolCardStyle = {
  height: "100%",
  borderRadius: 12,
  boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
  border: "1px solid #e0e0e0",
  transition: "box-shadow 0.2s ease",
};

export default function AddBranchSchool(props) {
  const [showForm, setShowForm] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [parentSchoolId, setParentSchoolId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [branchSchools, setBranchSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loggingInSchool, setLoggingInSchool] = useState(false);

  const fetchBranchSchools = () => {
    if (!userId) return;
    setLoading(true);
    SchooleRegistrationService.getSchoolsWithStatsForAdmin(userId)
      .then((schools) => {
        setBranchSchools(Array.isArray(schools) ? schools : []);
      })
      .catch(() => setBranchSchools([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const userDetails = localStorage.getItem("userDetail")
      ? JSON.parse(localStorage.getItem("userDetail"))
      : {};

    if (userDetails && (userDetails.role === "SCHOOLE" || userDetails.role === "SUPERADMIN")) {
      const schoolId = userDetails.schoolId || userDetails.schooleId || userDetails._id;
      setParentSchoolId(schoolId);
      setUserId(userDetails._id);
    }
  }, []);

  useEffect(() => {
    if (userId && !showForm) {
      fetchBranchSchools();
    }
  }, [userId, showForm]);

  // Dashboard summary from branchSchools
  const totalSchools = branchSchools.length;
  const parentCount = branchSchools.filter((s) => s._id === parentSchoolId).length;
  const childCount = totalSchools - parentCount;
  const totalStudents = branchSchools.reduce((acc, s) => acc + (s.studentCount || 0), 0);
  const totalTeachers = branchSchools.reduce((acc, s) => acc + (s.teacherCount || 0), 0);
  const totalPresent = branchSchools.reduce((acc, s) => acc + (s.attendancePresentToday || 0), 0);
  const totalAbsent = branchSchools.reduce((acc, s) => acc + (s.attendanceAbsentToday || 0), 0);
  const totalActivities = branchSchools.reduce((acc, s) => acc + (s.activityCount || 0), 0);

  if (!parentSchoolId) {
    return null;
  }

  // Single-school dashboard view (when a school card is clicked)
  const renderSchoolDetailDashboard = (school) => {
    const isParent = school._id === parentSchoolId;

    const handleLoginAsSchool = async () => {
      try {
        setLoggingInSchool(true);
        const res = await SchooleRegistrationService.loginAsSchool(school._id);
        // Persist new auth context as SCHOOLE
        localStorage.setItem("userDetail", JSON.stringify(res));
        localStorage.setItem("id_token", res.token);
        // Go to the standard school landing page
        props.history.push("/app/studentdetails");
        // Ensure all components re-read localStorage and auth state
        window.location.reload();
      } catch (e) {
        alert(e?.response?.data?.message || e?.message || "Unable to login as this school");
      } finally {
        setLoggingInSchool(false);
      }
    };

    return (
      <Box p={3}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => setSelectedSchool(null)}
            style={{ color: "#0f3b62", marginRight: 16 }}
          >
            Back
          </Button>
          <Box display="flex" alignItems="center" flexWrap="wrap">
            <Typography variant="h5" style={{ fontWeight: 700, color: "#0f3b62", marginRight: 12 }}>
              {school.schooleName || "—"}
            </Typography>
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: 12,
                fontSize: 12,
                fontWeight: 600,
                color: "#fff",
                backgroundColor: isParent ? "#2e7d32" : "#1565c0",
              }}
            >
              {isParent ? "Parent School" : "Child School"}
            </span>
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <Button
              variant="contained"
              onClick={handleLoginAsSchool}
              disabled={loggingInSchool}
              style={{ backgroundColor: "#0f3b62", color: "white", borderRadius: 8 }}
            >
              {loggingInSchool ? "Logging in..." : "Login"}
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2} style={{ marginBottom: 24 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} style={summaryCardStyle("linear-gradient(135deg, #6a1b9a 0%, #8e24aa 100%)")}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" style={{ opacity: 0.9 }}>Students</Typography>
                  <Typography variant="h4" style={{ fontWeight: 700 }}>{school.studentCount ?? 0}</Typography>
                </Box>
                <PeopleIcon style={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} style={summaryCardStyle("linear-gradient(135deg, #e65100 0%, #ff9800 100%)")}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" style={{ opacity: 0.9 }}>Teachers</Typography>
                  <Typography variant="h4" style={{ fontWeight: 700 }}>{school.teacherCount ?? 0}</Typography>
                </Box>
                <PersonIcon style={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} style={summaryCardStyle("linear-gradient(135deg, #00838f 0%, #00acc1 100%)")}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" style={{ opacity: 0.9 }}>Attendance Today</Typography>
                  <Typography variant="h5" style={{ fontWeight: 700 }}>
                    <span style={{ color: "#a5d6a7" }}>P: {school.attendancePresentToday ?? 0}</span>
                    <span style={{ margin: "0 6px" }}>/</span>
                    <span style={{ color: "#ffab91" }}>A: {school.attendanceAbsentToday ?? 0}</span>
                  </Typography>
                </Box>
                <EventAvailableIcon style={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} style={summaryCardStyle("linear-gradient(135deg, #0f3b62 0%, #1565c0 100%)")}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" style={{ opacity: 0.9 }}>Activity Records</Typography>
                  <Typography variant="h4" style={{ fontWeight: 700 }}>{school.activityCount ?? 0}</Typography>
                </Box>
                <AssignmentIcon style={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Typography variant="h6" style={{ fontWeight: 600, color: "#333", marginBottom: 12 }}>
          School Details
        </Typography>
        <Card elevation={0} style={{ ...schoolCardStyle, borderLeft: `4px solid ${isParent ? "#2e7d32" : "#1565c0"}` }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center" style={{ color: "#666" }}>
                  <LocationOnIcon style={{ marginRight: 8, color: "#0f3b62" }} />
                  <Box>
                    <Typography variant="caption" color="textSecondary">City</Typography>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>{school.city || "—"}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center" style={{ color: "#666" }}>
                  <PersonIcon style={{ marginRight: 8, color: "#0f3b62" }} />
                  <Box>
                    <Typography variant="caption" color="textSecondary">Contact Person</Typography>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>{school.name || "—"}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center" style={{ color: "#666" }}>
                  <PhoneIcon style={{ marginRight: 8, color: "#0f3b62" }} />
                  <Box>
                    <Typography variant="caption" color="textSecondary">Contact Number</Typography>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>{school.schooleContact || school.mobileNumber || "—"}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center" style={{ color: "#666" }}>
                  <EmailIcon style={{ marginRight: 8, color: "#0f3b62" }} />
                  <Box>
                    <Typography variant="caption" color="textSecondary">School Email</Typography>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>{school.schooleEmail || school.email || "—"}</Typography>
                  </Box>
                </Box>
              </Grid>
              {school.address && (
                <Grid item xs={12}>
                  <Box display="flex" alignItems="flex-start" style={{ color: "#666" }}>
                    <LocationOnIcon style={{ marginRight: 8, color: "#0f3b62", marginTop: 2 }} />
                    <Box>
                      <Typography variant="caption" color="textSecondary">Address</Typography>
                      <Typography variant="body2" style={{ fontWeight: 500 }}>{school.address}</Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  };

  if (selectedSchool) {
    return renderSchoolDetailDashboard(selectedSchool);
  }

  return (
    <>
      {!showForm ? (
        <Box p={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" style={{ fontWeight: 700, color: "#0f3b62" }}>
              Branch Schools Dashboard
            </Typography>
            <Button
              variant="contained"
              onClick={() => setShowForm(true)}
              style={{ backgroundColor: "rgb(15 59 98)", color: "white", borderRadius: 8 }}
              startIcon={<SchoolIcon />}
            >
              Add Branch School
            </Button>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={280} p={3}>
              <CircularProgress style={{ color: "#0f3b62" }} />
            </Box>
          ) : (
            <>
              {/* Summary cards */}
              <Grid container spacing={2} style={{ marginBottom: 24 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={0} style={summaryCardStyle("linear-gradient(135deg, #0f3b62 0%, #1565c0 100%)")}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body2" style={{ opacity: 0.9 }}>Total Schools</Typography>
                        <Typography variant="h4" style={{ fontWeight: 700 }}>{totalSchools}</Typography>
                      </Box>
                      <SchoolIcon style={{ fontSize: 40, opacity: 0.8 }} />
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={0} style={summaryCardStyle("linear-gradient(135deg, #2e7d32 0%, #43a047 100%)")}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body2" style={{ opacity: 0.9 }}>Parent School</Typography>
                        <Typography variant="h4" style={{ fontWeight: 700 }}>{parentCount}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={0} style={summaryCardStyle("linear-gradient(135deg, #1565c0 0%, #1976d2 100%)")}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body2" style={{ opacity: 0.9 }}>Child Schools</Typography>
                        <Typography variant="h4" style={{ fontWeight: 700 }}>{childCount}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={0} style={summaryCardStyle("linear-gradient(135deg, #6a1b9a 0%, #8e24aa 100%)")}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body2" style={{ opacity: 0.9 }}>Total Students</Typography>
                        <Typography variant="h4" style={{ fontWeight: 700 }}>{totalStudents}</Typography>
                      </Box>
                      <PeopleIcon style={{ fontSize: 40, opacity: 0.8 }} />
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={0} style={summaryCardStyle("linear-gradient(135deg, #e65100 0%, #ff9800 100%)")}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body2" style={{ opacity: 0.9 }}>Total Teachers</Typography>
                        <Typography variant="h4" style={{ fontWeight: 700 }}>{totalTeachers}</Typography>
                      </Box>
                      <PersonIcon style={{ fontSize: 40, opacity: 0.8 }} />
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={0} style={summaryCardStyle("linear-gradient(135deg, #00838f 0%, #00acc1 100%)")}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body2" style={{ opacity: 0.9 }}>Attendance Today</Typography>
                        <Typography variant="h5" style={{ fontWeight: 700 }}>
                          <span style={{ color: "#a5d6a7" }}>P: {totalPresent}</span>
                          <span style={{ margin: "0 6px" }}>/</span>
                          <span style={{ color: "#ffab91" }}>A: {totalAbsent}</span>
                        </Typography>
                      </Box>
                      <EventAvailableIcon style={{ fontSize: 40, opacity: 0.8 }} />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              {/* School cards */}
              <Typography variant="h6" style={{ fontWeight: 600, color: "#333", marginBottom: 16 }}>
                Schools
              </Typography>
              {branchSchools.length === 0 ? (
                <Paper style={{ padding: 48, textAlign: "center", color: "#666" }}>
                  <SchoolIcon style={{ fontSize: 48, marginBottom: 8, opacity: 0.5 }} />
                  <Typography>No branch schools registered yet. Add one to get started.</Typography>
                </Paper>
              ) : (
                <Grid container spacing={3}>
                  {branchSchools.map((row) => {
                    const isParent = row._id === parentSchoolId;
                    return (
                      <Grid item xs={12} sm={6} md={4} key={row._id}>
                        <Card
                          elevation={0}
                          onClick={() => setSelectedSchool(row)}
                          style={{
                            ...schoolCardStyle,
                            borderLeft: `4px solid ${isParent ? "#2e7d32" : "#1565c0"}`,
                            cursor: "pointer",
                          }}
                        >
                          <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                              <Typography variant="h6" style={{ fontWeight: 700, color: "#0f3b62" }}>
                                {row.schooleName || "—"}
                              </Typography>
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "2px 8px",
                                  borderRadius: 12,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "#fff",
                                  backgroundColor: isParent ? "#2e7d32" : "#1565c0",
                                }}
                              >
                                {isParent ? "Parent" : "Child"}
                              </span>
                            </Box>
                            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                              {row.city || "—"} · {row.name || "—"}
                            </Typography>
                            <Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
                              <Box display="flex" alignItems="center" style={{ color: "#6a1b9a" }}>
                                <PeopleIcon style={{ fontSize: 18, marginRight: 4 }} />
                                <Typography variant="body2" style={{ fontWeight: 600 }}>{row.studentCount ?? 0}</Typography>
                                <Typography variant="caption" style={{ marginLeft: 2 }}>Students</Typography>
                              </Box>
                              <Box display="flex" alignItems="center" style={{ color: "#e65100" }}>
                                <PersonIcon style={{ fontSize: 18, marginRight: 4 }} />
                                <Typography variant="body2" style={{ fontWeight: 600 }}>{row.teacherCount ?? 0}</Typography>
                                <Typography variant="caption" style={{ marginLeft: 2 }}>Teachers</Typography>
                              </Box>
                              <Box display="flex" alignItems="center">
                                <Typography variant="body2" style={{ color: "#2e7d32", fontWeight: 600 }}>
                                  P: {row.attendancePresentToday ?? 0}
                                </Typography>
                                <Typography variant="body2" style={{ margin: "0 4px" }}>/</Typography>
                                <Typography variant="body2" style={{ color: "#c62828", fontWeight: 600 }}>
                                  A: {row.attendanceAbsentToday ?? 0}
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" style={{ color: "#00838f" }}>
                                <AssignmentIcon style={{ fontSize: 18, marginRight: 4 }} />
                                <Typography variant="body2" style={{ fontWeight: 600 }}>{row.activityCount ?? 0}</Typography>
                                <Typography variant="caption" style={{ marginLeft: 2 }}>Activities</Typography>
                              </Box>
                            </Box>
                            {(row.schooleContact || row.mobileNumber) && (
                              <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                                Contact: {row.schooleContact || row.mobileNumber || "—"}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </>
          )}
        </Box>
      ) : (
        <SchooleRegistration
          {...props}
          parentSchoolId={parentSchoolId}
          // Ensure classes checkbox section is available for branch creation too
          onBack={() => setShowForm(false)}
        />
      )}
    </>
  );
}
